import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'reactstrap';
import {
  getBlocos,
  getClausulas,
  getModeloItens,
  getSecoes,
} from '../../../pages/GestaoMinutas/minutaApi';
import { montarArvoreNumerada } from '../../../pages/GestaoMinutas/minutaRender';
import {
  buildContrFornPlaceholderContext,
  renderMinutaBodyHtml,
} from './minutaPlaceholders';

const sectionDividerStyle = {
  borderTop: '1px solid #e6e6e6',
  margin: '0 0 1.25rem',
};

const bodyStyle = {
  fontSize: '14px',
  lineHeight: 1.5,
  color: '#212529',
};

const BLOCO_TIPO_LABEL = {
  preambulo: 'Preâmbulo',
  qualificacao: 'Qualificação',
  considerandos: 'Considerandos',
  fecho: 'Fecho',
  assinaturas: 'Assinaturas',
  testemunhas: 'Testemunhas',
  anexo: 'Anexo',
  outro: 'Outro',
};

function SectionBlock({ title, children, showTopDivider = false }) {
  return (
    <div className="mb-4">
      {showTopDivider ? <div style={sectionDividerStyle} aria-hidden="true" /> : null}
      {title ? (
        <h5 className="mb-3" style={{ fontWeight: 600 }}>
          {title}
        </h5>
      ) : null}
      {children}
    </div>
  );
}

function renderBodyHtml(body, placeholderContext) {
  const html = renderMinutaBodyHtml(body, placeholderContext);
  if (!html) {
    return <p className="text-muted mb-0">Sem conteúdo.</p>;
  }
  return (
    <div
      style={bodyStyle}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function indexById(rows, idKey) {
  return Object.fromEntries((rows || []).map((row) => [row[idKey], row]));
}

/**
 * Lista somente-leitura dos minutaModItem do modelo selecionado,
 * no padrão visual das seções da aba 1 da validação.
 */
const ContrFornValidationMinutaPreview = ({
  minutaModId,
  contrFornData = {},
  compraCotacaoInfo = null,
  selContratantes = [],
  selPfsFornecedor = [],
  selPjsFornecedor = [],
}) => {
  const [itens, setItens] = useState([]);
  const [blocos, setBlocos] = useState([]);
  const [secoes, setSecoes] = useState([]);
  const [clausulas, setClausulas] = useState([]);
  const [placeholderContext, setPlaceholderContext] = useState(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
  const [isLoadingItens, setIsLoadingItens] = useState(false);
  const [isLoadingPlaceholders, setIsLoadingPlaceholders] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      setIsLoadingCatalog(true);
      try {
        const [b, s, c] = await Promise.all([getBlocos(), getSecoes(), getClausulas()]);
        if (cancelled) return;
        setBlocos(b || []);
        setSecoes(s || []);
        setClausulas(c || []);
      } catch (error) {
        console.error('Erro ao carregar catálogo de minutas:', error);
        if (!cancelled) {
          setBlocos([]);
          setSecoes([]);
          setClausulas([]);
        }
      } finally {
        if (!cancelled) setIsLoadingCatalog(false);
      }
    };

    loadCatalog();
    return () => { cancelled = true; };
  }, []);

  const loadItens = useCallback(async (modId) => {
    const normalizedId = Number(modId) || 0;
    if (!normalizedId) {
      setItens([]);
      setIsLoadingItens(false);
      return;
    }

    setIsLoadingItens(true);
    try {
      const rows = await getModeloItens(normalizedId);
      setItens(rows || []);
    } catch (error) {
      console.error('Erro ao carregar itens do modelo de minuta:', error);
      setItens([]);
    } finally {
      setIsLoadingItens(false);
    }
  }, []);

  useEffect(() => {
    loadItens(minutaModId);
  }, [minutaModId, loadItens]);

  useEffect(() => {
    let cancelled = false;

    const loadPlaceholders = async () => {
      if (!Number(minutaModId)) {
        setPlaceholderContext(null);
        return;
      }

      setIsLoadingPlaceholders(true);
      try {
        const context = await buildContrFornPlaceholderContext(contrFornData, {
          selContratantes,
          selPfsFornecedor,
          selPjsFornecedor,
          compraCotacaoInfo,
        });
        if (!cancelled) setPlaceholderContext(context);
      } catch (error) {
        console.error('Erro ao resolver placeholders da minuta:', error);
        if (!cancelled) setPlaceholderContext(null);
      } finally {
        if (!cancelled) setIsLoadingPlaceholders(false);
      }
    };

    loadPlaceholders();
    return () => { cancelled = true; };
  }, [
    minutaModId,
    contrFornData.contrForn_contratante,
    contrFornData.contrForn_parteTab,
    contrFornData.contrForn_parteId,
    contrFornData.contrForn_dtAssin,
    contrFornData.contrForn_dtVigIni,
    contrFornData.contrForn_dtVigFim,
    contrFornData.contrForn_vlrGlobal,
    contrFornData.contrForn_dataPagamento,
    compraCotacaoInfo?.informacoes_contrato?.objeto_resumido,
    selContratantes,
    selPfsFornecedor,
    selPjsFornecedor,
  ]);

  const blocoById = useMemo(() => indexById(blocos, 'minutaBloco_id'), [blocos]);
  const secById = useMemo(() => indexById(secoes, 'minutaSec_id'), [secoes]);
  const clausById = useMemo(() => indexById(clausulas, 'minutaClaus_id'), [clausulas]);

  const arvore = useMemo(() => montarArvoreNumerada(itens), [itens]);

  if (!Number(minutaModId)) {
    return null;
  }

  if (isLoadingCatalog || isLoadingItens || (isLoadingPlaceholders && !placeholderContext)) {
    return (
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <Spinner size="sm" />
        <span className="text-muted">Carregando itens do modelo...</span>
      </div>
    );
  }

  if (!itens.length) {
    return (
      <p className="text-muted mb-3">Nenhum item neste modelo.</p>
    );
  }

  return (
    <div className="mb-2">
      {arvore.map((node, index) => {
        const { item, numero, filhos } = node;
        const showTopDivider = index > 0;

        if (item.minutaModItem_tipo === 'bloco') {
          const bloco = blocoById[item.minutaBloco_id];
          const tipoLabel = bloco
            ? (BLOCO_TIPO_LABEL[bloco.minutaBloco_tipo] || bloco.minutaBloco_tipo)
            : null;
          const title = bloco
            ? `Bloco — ${bloco.minutaBloco_nome}${tipoLabel ? ` (${tipoLabel})` : ''}`
            : 'Bloco — (bloco removido)';

          return (
            <SectionBlock
              key={item.minutaModItem_id}
              title={title}
              showTopDivider={showTopDivider}
            >
              {bloco ? renderBodyHtml(bloco.minutaBloco_body, placeholderContext) : (
                <p className="text-muted mb-0">Referência de bloco não encontrada no catálogo.</p>
              )}
            </SectionBlock>
          );
        }

        if (item.minutaModItem_tipo === 'secao') {
          const sec = secById[item.minutaSec_id];
          const title = sec
            ? `Seção ${numero} — ${sec.minutaSec_nome}`
            : `Seção ${numero} — (seção removida)`;

          return (
            <SectionBlock
              key={item.minutaModItem_id}
              title={title}
              showTopDivider={showTopDivider}
            >
              {(filhos || []).length === 0 ? (
                <p className="text-muted mb-0">Nenhuma cláusula nesta seção.</p>
              ) : (
                (filhos || []).map((filho, filhoIdx) => {
                  const cl = clausById[filho.item.minutaClaus_id];
                  const filhoTitle = cl
                    ? `Cláusula ${filho.numero} — ${cl.minutaClaus_nome}`
                    : `Cláusula ${filho.numero} — (cláusula removida)`;

                  return (
                    <div
                      key={filho.item.minutaModItem_id}
                      className="mb-3"
                      style={{ marginLeft: 12 }}
                    >
                      {filhoIdx > 0 ? (
                        <div style={{ ...sectionDividerStyle, margin: '0 0 0.75rem' }} aria-hidden="true" />
                      ) : null}
                      <h6 className="mb-2" style={{ fontWeight: 600 }}>{filhoTitle}</h6>
                      {cl ? renderBodyHtml(cl.minutaClaus_body, placeholderContext) : (
                        <p className="text-muted mb-0">Referência de cláusula não encontrada no catálogo.</p>
                      )}
                    </div>
                  );
                })
              )}
            </SectionBlock>
          );
        }

        // cláusula avulsa (raiz)
        const cl = clausById[item.minutaClaus_id];
        const title = cl
          ? `Cláusula ${numero} — ${cl.minutaClaus_nome}`
          : `Cláusula ${numero} — (cláusula removida)`;

        return (
          <SectionBlock
            key={item.minutaModItem_id}
            title={title}
            showTopDivider={showTopDivider}
          >
            {cl ? renderBodyHtml(cl.minutaClaus_body, placeholderContext) : (
              <p className="text-muted mb-0">Referência de cláusula não encontrada no catálogo.</p>
            )}
          </SectionBlock>
        );
      })}
    </div>
  );
};

export default ContrFornValidationMinutaPreview;
